import * as React from "react";
import { Link } from "react-router";
import titleAndMetaHelmet from "./title-meta";
import {
  KeyboardEventManager,
  RefocusManager,
  Outliner,
  ProductLogo,
  Paragraph,
  Size,
  TextColor,
  Space,
  MediaObject,
  Title
} from "@root/shared";
import { ILinkItem } from "./link-item";
import { Copyright } from "./copyright";
import { LoginShellInitProps } from "@lib/common-interfaces/login-shell-init-props";
import { telemetry } from "@root/lib/telemetry";
const classNames = require("classnames");
const styles = require("./login-shell-full.scss");

export interface LoginShellFullProps {
  linkItems?: ILinkItem[];
  externalLinkItems?: ILinkItem[];
  subtitleArea?: React.ReactNode;
  titleArea: React.ReactNode;
  attemptingSignup?: boolean;
}

export class LoginShellFull extends React.Component<LoginShellFullProps, {}> {
  public initProps: LoginShellInitProps;

  public UNSAFE_componentWillMount() {
    const { initProps } = window as any;
    this.initProps = initProps;
    const fromHockeyApp = this.initProps.fromHockeyApp;
    if (fromHockeyApp) {
      const { attemptingSignup } = this.props;
      telemetry.trackEvent("HockeyApp authentication redirect view", { action: attemptingSignup ? "signup" : "signin" });
    }
  }

  public render() {
    const { externalLinkItems, linkItems, subtitleArea, titleArea, attemptingSignup } = this.props;
    const messages = this.initProps.alternativeCampaignMessages;
    const fromHockeyApp = this.initProps.fromHockeyApp;
    const fromXamarinInsights = this.initProps.fromXamarinInsights;
    const leftColClassName = classNames(
      styles["left-col"],
      { [styles["from-hockeyapp"]]: fromHockeyApp },
      { [styles["none"]]: fromHockeyApp && attemptingSignup },
      { [styles["from-xamarininsights"]]: fromXamarinInsights }
    );
    const hockeyappSignupBackgroundImage = classNames(styles["left-col"], {
      [styles["from-ha-register"]]: fromHockeyApp && attemptingSignup
    });
    const leftColWrapperClassName = classNames(styles.leftColWrapper, { [styles.fromHockeyapp]: fromHockeyApp });
    const productName = "App Center";
    const header = messages ? messages.header : `${productName} is mission control for apps.`,
      bullets = messages
        ? messages.bullets
        : [
            "Automatically build your app, test it on real devices, and distribute it to beta testers",
            "Collect crash reports and user analytics, including custom events",
            "Add push notifications to your app in minutes",
            "Securely manage user identities in the cloud and scale to millions of users with high availability",
            "Manage, persist, and sync app data in the cloud in both online and offline scenarios"
          ];
    const hockeyappTitle = attemptingSignup ? "Meet App Center, the future of HockeyApp" : "👋 Welcome to App Center";
    const hockeyappParagraph = attemptingSignup
      ? "HockeyApp will be fully transitioning to App Center on November 16, 2019."
      : "Sign in with your HockeyApp account, all your apps are waiting for you.";

    return (
      <KeyboardEventManager>
        <RefocusManager>
          <Outliner>
            <div className={styles["login-shell"]}>
              {titleAndMetaHelmet}
              <div className={styles.banner}>
                <MediaObject textOnly>
                  <Paragraph size={Size.Small} invert>
                    {`Visual Studio ${productName}`}
                  </Paragraph>
                </MediaObject>
              </div>
              <div className={leftColClassName}>
                <div className={leftColWrapperClassName}>
                  <ProductLogo invert />
                  {fromHockeyApp ? (
                    <div className={styles.hockeyappContainer}>
                      <Title size={Size.Large} color={TextColor.Primary} spaceBelow={Space.Small} invert>
                        {hockeyappTitle}
                      </Title>
                      <Paragraph size={Size.Large} color={TextColor.Primary} className={styles.hockeyappText} invert>
                        {hockeyappParagraph}
                      </Paragraph>
                    </div>
                  ) : (
                    <>
                      <p>{header}</p>
                      {bullets ? (
                        <ul className={styles["bullets"]}>
                          {bullets.map((bullet, i) => {
                            return <li key={i}>{bullet}</li>;
                          })}
                        </ul>
                      ) : null}
                    </>
                  )}
                </div>
                {fromHockeyApp && attemptingSignup ? <div className={hockeyappSignupBackgroundImage} /> : null}
                <Paragraph size={Size.Small} color={TextColor.Primary} invert className={styles["copyright-on-the-left"]}>
                  <Copyright />
                </Paragraph>
              </div>

              <div className={styles["right-col"]}>
                <div className={styles["right-col-wrapper"]}>
                  <div className={styles["login-form"]}>
                    <div className={styles["form-header"]}>
                      <Title size={Size.Small}>{titleArea}</Title>
                      <Paragraph className={styles["invitation-text"]} size={Size.Medium} color={TextColor.Secondary}>
                        {subtitleArea}
                      </Paragraph>
                    </div>

                    {this.props.children}
                  </div>

                  <div className={styles["links-area"]}>
                    {linkItems && linkItems.length > 0
                      ? linkItems.map((item, index) => (
                          <Link key={index} to={item.route} target="_blank">
                            {item.title}
                          </Link>
                        ))
                      : null}
                    {externalLinkItems && externalLinkItems.length > 0
                      ? externalLinkItems.map((item, index) => (
                          <a key={index} href={item.route} target="_blank">
                            {item.title}
                          </a>
                        ))
                      : null}
                    <Paragraph size={Size.Small} color={TextColor.Secondary} className={styles["copyright-on-the-right"]}>
                      <Copyright />
                    </Paragraph>
                  </div>
                </div>
              </div>
            </div>
          </Outliner>
        </RefocusManager>
      </KeyboardEventManager>
    );
  }
}
